type FilterQuery = Record<string, Record<string, string | number | boolean>>;

export type StoryblokListQuery = {
  content_type?: string;
  by_uuids?: string;
  per_page?: number;
  sort_by?: string;
  resolve_relations?: string;
  filter_query?: FilterQuery;
};

/**
 * Laedt Storyblok-Listen aus Client-Komponenten ueber den eigenen, gecachten
 * Proxy statt direkt gegen api.storyblok.com.
 *
 * Der direkte Weg lief an Next vorbei: jeder Besucher loeste eigene
 * Storyblok-Requests aus, unabhaengig von jedem Caching. Ueber /api/storyblok/list
 * teilen sich alle Besucher denselben Cache-Eintrag.
 *
 * `version` wird bewusst nicht uebergeben — das entscheidet der Server.
 */
export async function fetchStoryblokList<T = any>(
  query: StoryblokListQuery,
  signal?: AbortSignal,
): Promise<T[]> {
  const params = new URLSearchParams();

  if (query.content_type) params.set("content_type", query.content_type);
  if (query.by_uuids) params.set("by_uuids", query.by_uuids);
  if (query.per_page != null) params.set("per_page", String(query.per_page));
  if (query.sort_by) params.set("sort_by", query.sort_by);
  if (query.resolve_relations) {
    params.set("resolve_relations", query.resolve_relations);
  }

  // filter_query flach kodieren: filter[feld][operator]=wert
  for (const [field, condition] of Object.entries(query.filter_query ?? {})) {
    for (const [operator, value] of Object.entries(condition)) {
      params.set(`filter[${field}][${operator}]`, String(value));
    }
  }

  const res = await fetch(`/api/storyblok/list?${params}`, { signal });
  if (!res.ok) throw new Error(`Storyblok list failed: ${res.status}`);

  const data = await res.json();
  return (data.stories ?? []) as T[];
}

/**
 * Laedt Events und Reisen gemeinsam.
 *
 * Reisen sind ein eigener Content-Type (`travel`), teilen sich aber Felder
 * und Renderpfad mit `event`. Fast jede Galerie will beide sehen.
 *
 * WARUM ZWEI ABFRAGEN statt einer mit `__or`:
 * `filter_query[__or][]` funktioniert gegen die Storyblok-API, aber nicht
 * zusammen mit `content_type` — die Kombination quittiert Storyblok mit
 * "filter_query must be a nested parameter" und 0 Treffern. Der Proxy macht
 * `content_type` zwingend, weil sich sonst nicht entscheiden laesst, ob
 * `text` oeffentlich ist. Beides zusammen schliesst `__or` hier aus.
 *
 * Zwei getrennte Abfragen sind ohnehin das bessere Caching: jede traegt
 * ihren eigenen `sb:type:`-Tag und wird gezielt invalidiert, waehrend ein
 * `__or`-Request gar keinen Typ-Tag truege.
 *
 * `travel` darf leer sein — Storyblok liefert fuer unbekannte oder noch
 * unbenutzte Content-Types 0 Treffer ohne Fehler. Die Funktion ist deshalb
 * schon vor dem Anlegen des Typs gefahrlos.
 *
 * SORTIERUNG: Storyblok sortiert jede Haelfte fuer sich. Einfach aneinander
 * gehaengt stuenden alle Events vor allen Reisen — `stories[0]` waere dann
 * das naechste Event, nicht der naechste Termin ueberhaupt. Bei `sort_by`
 * auf `content.date` wird deshalb hier nachsortiert.
 *
 * ZUM KAPPEN — hier lauert der Fehler, den diese Funktion schon einmal
 * hatte: `per_page` bedeutet bei den Aufrufern zweierlei.
 *
 *   a) "genau so viele will ich sehen"  (per_page: 1 → der naechste Termin)
 *   b) "so gross ist mein Kandidatenpool, ich filtere danach selbst"
 *      (EventGallery holt 20 und wirft vergangene Termine erst danach weg)
 *
 * Im Fall b) darf nach dem Sortieren NICHT gekappt werden. Sonst gewinnen
 * die fruehesten Daten — und da Reisen weiter in der Zukunft liegen als das
 * Gros der Events, fielen genau sie hinten heraus. Gemessen: die 20
 * aeltesten Events enden am 07.04.2026, alle drei Reisen liegen danach. Die
 * Galerie haette nach der Konvertierung stillschweigend keine einzige Reise
 * mehr gezeigt.
 *
 * Deshalb kappt die Funktion nur, wenn der Aufrufer es mit `exact: true`
 * ausdruecklich verlangt.
 */
export async function fetchEventsAndTravels<T = any>(
  query: Omit<StoryblokListQuery, "content_type"> & {
    /** Nur auf `event` anwenden — z.B. `category`, das `travel` nicht hat. */
    eventOnlyFilter?: FilterQuery;
    /**
     * `per_page` ist eine harte Obergrenze fuer das Gesamtergebnis, nicht nur
     * die Seitengroesse je Haelfte. Nur setzen, wenn der Aufrufer die Liste
     * unveraendert uebernimmt (typisch: `per_page: 1` fuer "naechster
     * Termin"). Wer danach noch filtert, laesst es weg.
     */
    exact?: boolean;
  },
  signal?: AbortSignal,
): Promise<T[]> {
  const {
    eventOnlyFilter,
    exact,
    resolve_relations: relations,
    ...shared
  } = query;

  const [events, travels] = await Promise.all([
    fetchStoryblokList<T>(
      {
        ...shared,
        content_type: "event",
        filter_query: { ...shared.filter_query, ...eventOnlyFilter },
        ...(relations && { resolve_relations: relations }),
      },
      signal,
    ),
    fetchStoryblokList<T>(
      {
        ...shared,
        content_type: "travel",
        // Relationen heissen pro Content-Type anders: `event.experts` gilt
        // fuer Reisen nicht und wuerde vom Proxy still verworfen.
        ...(relations && {
          resolve_relations: relations.replace(/\bevent\./g, "travel."),
        }),
      },
      signal,
    ),
  ]);

  const merged = [...events, ...travels];

  // Nur bei Datumssortierung: andere sort_by-Werte (z.B. first_published_at)
  // liegen nicht zwangslaeufig in `content.date` und liessen sich hier nicht
  // rekonstruieren.
  const dateSort = shared.sort_by?.startsWith("content.date");
  if (dateSort && travels.length && events.length) {
    const dir = shared.sort_by?.endsWith(":desc") ? -1 : 1;
    merged.sort((a: any, b: any) => {
      const da = a?.content?.date ?? "";
      const db = b?.content?.date ?? "";
      return da === db ? 0 : (da < db ? -1 : 1) * dir;
    });
  }

  // Nur auf ausdrueckliche Ansage kappen — siehe Kommentar oben.
  return exact && shared.per_page != null
    ? merged.slice(0, shared.per_page)
    : merged;
}

/**
 * Einzelne Story nach Slug — ebenfalls ueber den gecachten Proxy.
 * Liefert null, wenn die Story nicht existiert.
 */
export async function fetchStoryblokStory<T = any>(
  slug: string,
  signal?: AbortSignal,
): Promise<T | null> {
  const params = new URLSearchParams({ slug });
  const res = await fetch(`/api/storyblok/list?${params}`, { signal });
  if (!res.ok) throw new Error(`Storyblok story failed: ${res.status}`);

  const data = await res.json();
  return (data.story ?? null) as T | null;
}
